import React, { useState } from 'react'
import { XMarkIcon, ArrowPathIcon, QuestionMarkCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { type InterviewGuide, type InterviewQuestion } from '../services/interviewApi'
import { type ExtendedInterviewGuide } from '../types/interview'

interface InterviewModalProps {
  isOpen: boolean
  onClose: () => void
  guide: ExtendedInterviewGuide | null
  loading: boolean
  onReprocess?: () => void
  reprocessing?: boolean
}

// Format interview questions content - MATCHING JobDetailPage format
const formatInterviewQuestions = (content: string) => {
  if (!content) return null
  
  // Decode HTML entities
  const decodedContent = content
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
  
  const lines = decodedContent.split('\n')
  const elements: React.ReactElement[] = []
  let currentIndex = 0
  let isInListenFor = false
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    
    if (!line) continue
    
    // Handle section headers (all caps or title case)
    if (line.match(/^[A-Z][A-Z\s]+$/) || line.match(/^[A-Z][a-z\s]+$/)) {
      elements.push(
        <h3 key={currentIndex++} className="text-xl font-bold text-gray-900 mb-4 mt-6 flex items-center">
          <QuestionMarkCircleIcon className="w-6 h-6 text-purple-600 mr-3" />
          {line}
        </h3>
      )
    }
    // Handle questions (lines that start with quotes or contain question marks)
    else if (line.startsWith('"') || line.includes('?')) {
      elements.push(
        <div key={currentIndex++} className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
          <p className="text-sm font-medium text-blue-800 leading-relaxed">{line}</p>
        </div>
      )
    }
    // Handle "What to listen for:" sections
    else if (line.toLowerCase().includes('what to listen for')) {
      isInListenFor = true
      elements.push(
        <h4 key={currentIndex++} className="text-md font-semibold text-gray-800 mb-2 mt-4 flex items-center">
          <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2" />
          {line}
        </h4>
      )
    }
    // Handle bullet points or list items
    else if (line.startsWith('-') || line.startsWith('•') || line.startsWith('*')) {
      const text = line.replace(/^[-•*]\s*/, '').trim()
      elements.push(
        <div key={currentIndex++} className="flex items-start mb-2 ml-4">
          <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
          <span className="text-sm text-gray-700 leading-relaxed">{text}</span>
        </div>
      )
    }
    // Handle numbered lists
    else if (line.match(/^\d+\.\s/)) {
      const text = line.replace(/^\d+\.\s*/, '').trim()
      elements.push(
        <div key={currentIndex++} className="flex items-start mb-2 ml-4">
          <span className="text-sm font-medium text-green-600 mr-2 mt-0.5">{line.match(/^\d+\./)?.[0]}</span>
          <span className="text-sm text-gray-700 leading-relaxed">{text}</span>
        </div>
      )
    }
    // Handle lines that look like bullet points but don't start with bullet characters
    else if (isInListenFor && line.length > 0 && !line.match(/^[A-Z][A-Z\s]+$/) && !line.match(/^[A-Z][a-z\s]+$/)) {
      const text = line.replace(/^[•\-*]\s*/, '').trim()
      elements.push(
        <div key={currentIndex++} className="flex items-start mb-2 ml-4">
          <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
          <span className="text-sm text-gray-700 leading-relaxed">{text}</span>
        </div>
      )
    }
    // Handle regular paragraphs
    else if (line.length > 0) {
      // Check if this line starts a new question (reset listen for state)
      if (line.includes('?') || line.startsWith('"')) {
        isInListenFor = false
      }
      
      elements.push(
        <p key={currentIndex++} className="text-sm text-gray-700 mb-3 leading-relaxed">
          {line}
        </p>
      )
    }
  }
  
  return <div className="space-y-2">{elements}</div>
}

const InterviewModal: React.FC<InterviewModalProps> = ({
  isOpen,
  onClose,
  guide,
  loading,
  onReprocess,
  reprocessing = false
}) => {
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set())

  const toggleQuestion = (index: number) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }
  
  if (!isOpen) return null

  const isL1Guide = guide?.isJobLevel === true
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isL1Guide ? 'General Interview Questions (L1)' : 'Customized Interview Guide (L2)'}
              </h3>
              {guide && !isL1Guide && (
                <p className="text-sm text-gray-600 mt-1">
                  {guide.candidate_name} - {guide.job_title} ({guide.interview_level})
                </p>
              )}
              {guide && isL1Guide && (
                <p className="text-sm text-gray-600 mt-1">
                  Based on job requirements
                </p>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {guide && onReprocess && !isL1Guide && (
                <button
                  onClick={onReprocess}
                  disabled={reprocessing}
                  className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Regenerate questions"
                >
                  {reprocessing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  ) : (
                    <ArrowPathIcon className="h-5 w-5" />
                  )}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Generating interview questions...</span>
              </div>
            ) : guide && (guide.questions || guide.key_questions) ? (
              isL1Guide ? (
                // ✅ NEW: Use the same formatting as JobDetailPage
                <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                  <div
                    className="whitespace-pre-wrap text-sm leading-relaxed font-sans"
                    style={{
                      backgroundColor: '#ffffff !important',
                      color: '#1f2937 !important',
                      lineHeight: '1.6',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}
                  >
                    {formatInterviewQuestions(guide.questions)}
                  </div>
                </div>
              ) : (
                // Detailed view for L2 Guide (unchanged)
                <div className="space-y-6">
                  {/* Interview Scope */}
                  {guide.scope && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">🔶 Interview Scope</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p><strong>Target Level:</strong> {guide.scope?.target_level ?? 'N/A'}</p>
                        <p><strong>Areas to Test:</strong> {guide.scope?.areas_to_test?.join(', ') ?? 'N/A'}</p>
                        <p><strong>Key Technologies:</strong> {guide.scope?.key_technologies?.join(', ') ?? 'N/A'}</p>
                        <p><strong>Overall Match Score:</strong> {guide.overall_score ? (guide.overall_score * 100).toFixed(1) + '%' : 'N/A'}</p>
                      </div>
                    </div>
                  )}

                  {/* Key Questions */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">✅ Key Interview Questions</h4>
                    <div className="space-y-4">
                      {(guide.key_questions ?? []).map((question, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                  {question.category}
                                </span>
                              </div>
                              <p className="text-gray-900 font-medium mb-2">{question.question}</p>
                              
                              {expandedQuestions.has(index) && (
                                <div className="mt-3 space-y-3">
                                  <div>
                                    <h5 className="text-sm font-medium text-gray-700 mb-1">What to listen for:</h5>
                                    <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                                      {(question.what_to_listen_for ?? []).map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => toggleQuestion(index)}
                              className="ml-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              {expandedQuestions.has(index) ? 'Show Less' : 'Show Details'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Optional Exercise */}
                  {guide.optional_exercise && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-yellow-900 mb-2">🧪 Optional Exercise</h4>
                      <div className="text-sm text-yellow-800">
                        <p><strong>Title:</strong> {guide.optional_exercise.title}</p>
                        <p><strong>Time Limit:</strong> {guide.optional_exercise.time_limit}</p>
                        <p><strong>Description:</strong> {guide.optional_exercise.description}</p>
                        <div className="mt-2">
                          <p><strong>Evaluation Criteria:</strong></p>
                          <ul className="list-disc list-inside mt-1">
                            {(guide.optional_exercise.evaluation_criteria ?? []).map((criteria, idx) => (
                              <li key={idx}>{criteria}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Scorecard */}
                  {guide.scorecard && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">📊 Scorecard</h4>
                      <div className="text-sm text-gray-700">
                        <p><strong>Rating Scale:</strong> {guide.scorecard.rating_scale}</p>
                        <p><strong>Recommendation Threshold:</strong> {guide.scorecard.recommendation_threshold}</p>
                        <div className="mt-3">
                          <p><strong>Categories:</strong></p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                            {(guide.scorecard.categories ?? []).map((category, idx) => (
                              <div key={idx} className="flex justify-between">
                                <span>{category.name}</span>
                                <span className="font-medium">{(category.weight * 100).toFixed(0)}%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recommendation Guidelines */}
                  {guide.recommendation_guidelines && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">💡 Recommendation Guidelines</h4>
                      <p className="text-sm text-green-800">{guide.recommendation_guidelines}</p>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading interview questions...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InterviewModal